import * as React from 'react';

import _ from 'underscore';

import {ConditionalCheckboxInput} from 'shells/form/conditional-checkbox-input';
import {CrossBrowserHiddenFormSubmit} from 'shells/form/cross-browser-hidden-form-submit';
import {FormField} from 'shells/form/form-field';
import {TextField} from 'shells/textfield/textfield';

import type {
    StageDeleteInputFieldInput,
    SalesAutomationStage as SalesAutomationStageFragment,
    TimespanInput,
} from 'nutshell-graphql-types';

import {SalesAutomationSidebarFormWrapper} from '../../core/sidebar/sales-automation-sidebar-form-wrapper';

import {AutocloseDurationPicker} from './autoclose-duration-picker';
import {OverdueDurationPicker} from './overdue-duration-picker';

import styles from './sales-process-form.module.css';

type Props = {
    entityId: string | null | undefined;
    stage: SalesAutomationStageFragment | null | undefined;
    formSubmitBtnRef: (arg1?: HTMLInputElement | null | undefined) => void;
    onSaveForm: (arg1: StageFormPostData) => Promise<any>;
    onDelete: (arg1: StageDeleteInputFieldInput) => Promise<any>;
};

type State = {
    name: string;
    description: string;
    shouldSetConfidence: boolean;
    confidence: number;
    overdueDuration: TimespanInput | null | undefined;
    autocloseDuration: TimespanInput | null | undefined;
    autocloseOutcomeId: string | null | undefined;
};

export type StageFormPostData = {
    id: string | null | undefined;
    name: string;
    description: string;
    defaultConfidence: number | null | undefined;
    overdueDuration: TimespanInput | null | undefined;
    autocloseDuration: TimespanInput | null | undefined;
    autocloseOutcomeId: string | null | undefined;
};

/*
 * Placeholder component that will eventually render a form for creating/editing
 * a "stage" in the sales automation board
 */
export class FormStages extends React.PureComponent<Props, State> {
    nameTextFieldRef: HTMLInputElement | null | undefined;

    constructor(props: Props) {
        super(props);

        this.state = {
            name: props.stage ? props.stage.name : '',
            description: props.stage ? props.stage.description : '',
            shouldSetConfidence: Boolean(props.stage && props.stage.defaultConfidence !== null),
            confidence:
                props.stage && typeof props.stage.defaultConfidence === 'number'
                    ? props.stage.defaultConfidence
                    : 0,
            overdueDuration:
                props.stage && props.stage.overdueDuration
                    ? {
                          timespan: props.stage.overdueDuration.timespan,
                          unit: props.stage.overdueDuration.unit,
                      }
                    : null,
            autocloseDuration:
                props.stage && props.stage.autocloseDuration
                    ? {
                          timespan: props.stage.autocloseDuration.timespan,
                          unit: props.stage.autocloseDuration.unit,
                      }
                    : null,
            autocloseOutcomeId:
                (props.stage && props.stage.autocloseOutcome && props.stage.autocloseOutcome.id) ||
                null,
        };
    }

    componentDidMount() {
        // Using defer to ensure text is filled before it is selected
        // Without this, text will not be selected properly on first mount
        _.defer(() => {
            if (this.nameTextFieldRef) {
                this.nameTextFieldRef.focus();
            }
        });
    }

    render() {
        return (
            <SalesAutomationSidebarFormWrapper
                id={this.props.entityId}
                onSaveForm={this.saveForm}
                onDelete={{
                    deleteText: 'Delete stage',
                    deleteConfirmationTitle: 'Are you sure you want to delete this stage?',
                    onDeleteConfirm: this.handleDelete,
                }}
            >
                <FormField label='Name' innerId='NameTextField'>
                    <TextField
                        // @ts-expect-error
                        name='name'
                        id='NameTextField'
                        required={true}
                        textFieldRef={(c) => {
                            this.nameTextFieldRef = c;
                        }}
                        onFocus={this.handleFocus}
                        value={this.state.name}
                        onChange={this.handleNameChange}
                    />
                </FormField>
                <FormField label='Stage guidance' innerId='DescriptionTextField'>
                    <TextField
                        // @ts-expect-error
                        name='description'
                        id='DescriptionTextField'
                        placeholder='Example: Leads enter this stage after a 30 minute conversation.'
                        value={this.state.description}
                        multiline={true}
                        onChange={this.handleDescriptionChange}
                    />
                </FormField>
                <FormField innerId='ConfidenceToggle'>
                    <ConditionalCheckboxInput
                        onChange={this.handleConfidenceToggle}
                        label='Set confidence for leads when they enter this stage'
                        checked={this.state.shouldSetConfidence}
                    >
                        <div className={styles['confidence-textfield']}>
                            <TextField
                                // @ts-expect-error
                                name='confidence'
                                type='number'
                                min='0'
                                max='100'
                                value={
                                    this.state.shouldSetConfidence
                                        ? String(this.state.confidence)
                                        : ''
                                }
                                selectOnFocus={true}
                                onChange={this.handleConfidenceChange}
                            />
                            <div className={styles.percent}>%</div>
                        </div>
                    </ConditionalCheckboxInput>
                </FormField>
                <FormField innerId='ConfidenceToggle'>
                    <OverdueDurationPicker
                        overdueDuration={this.state.overdueDuration}
                        onOverdueDurationChange={this.handleOverdueDurationChange}
                    />
                </FormField>
                <FormField innerId='ConfidenceToggle'>
                    <AutocloseDurationPicker
                        autocloseDuration={this.state.autocloseDuration}
                        autocloseOutcomeId={this.state.autocloseOutcomeId}
                        onAutocloseDurationChange={this.handleAutocloseDurationChange}
                        onAutocloseOutcomeIdChange={this.handleAutocloseOutcomeIdChange}
                    />
                </FormField>
                <CrossBrowserHiddenFormSubmit inputRef={this.props.formSubmitBtnRef} />
            </SalesAutomationSidebarFormWrapper>
        );
    }

    // This is for selecting the entire input field
    handleFocus = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.target.select();
    };

    handleNameChange = (val: string) => {
        this.setState({name: val});
    };

    handleDescriptionChange = (val: string) => {
        this.setState({description: val});
    };

    handleConfidenceToggle = () => {
        this.setState((prevState: State) => ({
            shouldSetConfidence: !prevState.shouldSetConfidence,
        }));
    };

    handleConfidenceChange = (newConfidence: string) => {
        this.setState({
            confidence: Number(newConfidence),
            shouldSetConfidence: true,
        });
    };

    handleOverdueDurationChange = (newValue?: TimespanInput | null) => {
        this.setState({overdueDuration: newValue});
    };

    handleAutocloseDurationChange = (newValue?: TimespanInput | null) => {
        this.setState({autocloseDuration: newValue});
    };

    handleAutocloseOutcomeIdChange = (newValue?: string | null) => {
        this.setState({autocloseOutcomeId: newValue});
    };

    handleDelete = (id: string) => {
        return this.props.onDelete({id});
    };

    saveForm = () => {
        const postData = {
            id: this.props.entityId,
            name: this.state.name,
            description: this.state.description,
            defaultConfidence: this.state.shouldSetConfidence ? this.state.confidence : null,
            overdueDuration: this.state.overdueDuration,
            autocloseDuration: this.state.autocloseDuration,
            autocloseOutcomeId: this.state.autocloseOutcomeId,
        } as const;

        return this.props.onSaveForm(postData);
    };
}
